import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import {
    writeFieldToForm,
    ClassicFieldMeta,
    classicFieldErrorMessage,
    formatShortFieldGroupDate,
    sameFieldGroupDate,
    toDisplayDate,
} from "./formFieldHelpers";
import DateField from "../../../common/formFields/DateField";

export interface ReceivedDateFieldProps {
    /** Currently selected Received date (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: Date | string;
    /**
     * Classic's own required rule (DataEntryDetailsRightPane):
     * `requestType != DISCLOSURE_ACCOUNTING || facilityAdditionalFields?.enableNonHIMReceivedDateRequired`.
     * Computed by the caller so this component stays free of transaction/facility lookups.
     */
    readonly required: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /** Extra, non-form side effect on change (classic pane's autosave PATCH). The DATA_ENTRY_FIELDS_FORM write is always done here. */
    readonly onReceivedDateChange?: (newValue?: Date | string) => void;
    /** AB#38460: unusable text to keep on screen, and the channel that reports it. Forwarded verbatim to DateField - see its own docblocks. */
    readonly invalidText?: string;
    readonly onInvalidText?: (text?: string, committed?: boolean) => void;
    /** AB#38460: forwarded verbatim to DateField - see its docblock. */
    readonly resyncKey?: string | number;
    /** Real redux-form validation state, threaded down by Classic Mode; omitted by Quick Entry (it has its own row-status gating). */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Bug fix (AB#35137): Received Date was missing from the Quick Entry Request Info panel -
 * Authorization Date and Request Date rows existed, but Received Date had no row at all, so a
 * field Classic Mode exposes (and which the AI already extracts) could not be seen or corrected
 * in Quick Entry.
 *
 * Mirrors Classic's own Received Date field: label "Received date", mm/dd/yyyy placeholder, and
 * `maxDate = today` (a received date can't be in the future). Unlike Due Date it has no
 * DISCLOSURE_ACCOUNTING visibility gate in Classic - it always renders - so there is no
 * `visible` prop here.
 */
const ReceivedDateField: React.FC<ReceivedDateFieldProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (value?: Date | string) => {
        writeFieldToForm(props.dispatch, FormConstants.RECEIVED_DATE, value, formName);
        props.onReceivedDateChange?.(value);
    };

    return (
        <DateField
            label="Received date"
            placeholder="mm/dd/yyyy"
            maxDate={new Date()}
            required={props.required}
            errMsg={classicFieldErrorMessage(props.meta)}
            invalidText={props.invalidText}
            onInvalidText={props.onInvalidText}
            resyncKey={props.resyncKey}
            onChange={onChange}
            // AB#37843: display-only normalization - see toDisplayDate. The write
            // path (onChange above) is untouched; only what the picker SHOWS changes.
            value={toDisplayDate(props.value)}
            name={FormConstants.RECEIVED_DATE}
        />
    );
};

export default ReceivedDateField;

export const RECEIVED_DATE_ROW_ID = "receivedDate";

/**
 * Builds the FieldGroupRow for the Received Date row. Required-ness follows Classic (see the
 * `required` prop), so an empty value reads as NeedsEntry only when the caller says it's required -
 * otherwise an empty Received Date is EmptyNotRequired rather than blocking Confirm All.
 *
 * `aiValue` is the AI-extracted received date (already parsed into the read model as
 * `requestAnalysis.data.receivedDate`); DataEntryDetails seeds it into the form field, so the row
 * shows it and reads as AI-pre-selected until the specialist edits it.
 */
export function mapReceivedDateToFieldGroupRow(
    badgeNumber: number,
    required: boolean,
    formValue: Date | string | undefined,
    aiValue?: Date | string,
): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null && formValue !== "";
    const emptyStatus = required ? FieldGroupRowStatus.NeedsEntry : FieldGroupRowStatus.EmptyNotRequired;

    return {
        id: RECEIVED_DATE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : emptyStatus,
        label: "Received Date",
        value: hasValue ? formatShortFieldGroupDate(formValue!) : null,
        isAiPreSelected: Boolean(aiValue) && sameFieldGroupDate(formValue, aiValue),
    };
}
